import { fhirClients } from "src-next/shared/api/fhirClient";
import {
  selectPhoneNumberIdFromEndpoint,
  selectWabaIdFromEndpoint,
} from "../helpers/selectWabaIdFromEndpoint";
import type { CrmSetupMetaConnectionResult } from "../../setup/types";
import type { WhatsAppBusinessPhoneContact } from "../../setup/services/whatsappBusinessSetupService";
import { useFhirData } from "@/utils/useFhirData";

interface GetActiveWhatsAppEndpointByContextParams {
  organizationId?: string;
}

interface CreateOrUpdateMetaEndpointParams {
  organizationId?: string;
  metaConnection: CrmSetupMetaConnectionResult;
  primaryPhoneValue?: string;
  phoneContacts?: WhatsAppBusinessPhoneContact[];
}

const WABA_IDENTIFIER_SYSTEM = "https://business.whatsapp.com/waba-id";
const WABA_EXTENSION_URL =
  "https://fluxmed.com.br/fhir/StructureDefinition/waba-id";
const PHONE_NUMBER_ID_EXTENSION_URL =
  "https://fluxmed.com.br/fhir/StructureDefinition/whatsapp-phone-number-id";
const DEFAULT_ENDPOINT_ADDRESS = "https://graph.facebook.com";

/**
 * Normaliza `organizationId` para sempre conter o prefixo `${oids.cnpj}-`.
 *
 * Motivação: em alguns fluxos o `organizationId` chega "cru" (ex.: "123") e em
 * outros já chega prefixado (ex.: `${oids.cnpj}-123`). Essa função é
 * **idempotente**: chamar múltiplas vezes não pode duplicar o prefixo.
 */

function normalizeOrganizationId(organizationId?: string): string | null {
  if (!organizationId?.trim()) return null;
  return useFhirData.ensureOrganizationIdWithOidPrefix(organizationId);
}

function extractEndpointEntries(bundle: fhir5.Bundle): fhir5.Endpoint[] {
  return (bundle.entry ?? [])
    .map((entry) => entry.resource)
    .filter(
      (resource): resource is fhir5.Endpoint =>
        resource !== undefined && resource.resourceType === "Endpoint",
    );
}

function buildOrganizationReference(organizationId: string): string {
  return useFhirData.ensureOrganizationIdWithOidPrefix(organizationId);
}

function hasMatchingManagingOrganization(
  endpoint: fhir5.Endpoint,
  organizationId: string,
): boolean {
  const managingOrganizationReference =
    endpoint.managingOrganization?.reference ?? "";
  const normalizedOrganizationId =
    useFhirData.ensureOrganizationIdWithOidPrefix(organizationId);

  // Alguns servidores persistem `reference` como "Organization/<id>" e outros
  // podem persistir apenas "<id>". Aceitamos ambos para evitar falso-negativo.
  return (
    managingOrganizationReference === normalizedOrganizationId ||
    managingOrganizationReference.endsWith(`/${normalizedOrganizationId}`)
  );
}

function buildContactWithRankOne(
  currentContacts: fhir5.ContactPoint[] | undefined,
  wabaId: string,
  primaryPhoneValue?: string,
  phoneContacts?: WhatsAppBusinessPhoneContact[],
): fhir5.ContactPoint[] {
  const normalizedPhoneContacts = (phoneContacts ?? [])
    .map((phoneContact) => ({
      id: phoneContact.id.trim(),
      displayPhoneNumber: phoneContact.displayPhoneNumber.trim(),
    }))
    .filter(
      (phoneContact) =>
        phoneContact.id.length > 0 &&
        phoneContact.displayPhoneNumber.length > 0,
    );

  if (normalizedPhoneContacts.length > 0) {
    return normalizedPhoneContacts.map((phoneContact, phoneIndex) => ({
      system: "phone",
      value: phoneContact.displayPhoneNumber,
      rank: phoneIndex + 1,
      extension: [
        ...(phoneIndex === 0
          ? ([{ url: WABA_EXTENSION_URL, valueString: wabaId } as any] as any[])
          : []),
        {
          url: PHONE_NUMBER_ID_EXTENSION_URL,
          valueString: phoneContact.id,
        } as any,
      ],
    }));
  }

  const normalizedContacts = (currentContacts ?? []).map(
    (contactPoint, index) => ({
      ...contactPoint,
      rank: index + 1,
    }),
  );
  const firstContact = normalizedContacts[0];

  const nextValue =
    primaryPhoneValue !== undefined
      ? primaryPhoneValue.trim()
      : (firstContact?.value ?? "");

  if (!firstContact) {
    return [
      {
        system: "phone",
        value: nextValue,
        rank: 1,
        extension: [{ url: WABA_EXTENSION_URL, valueString: wabaId } as any],
      },
    ];
  }

  const nextExtensions = (firstContact.extension ?? []).filter(
    (extension) => extension.url !== WABA_EXTENSION_URL,
  );
  nextExtensions.push({ url: WABA_EXTENSION_URL, valueString: wabaId } as any);

  const nextFirstContact: fhir5.ContactPoint = {
    ...firstContact,
    rank: 1,
    system: firstContact.system ?? "phone",
    value: nextValue,
    extension: nextExtensions,
  };

  return [nextFirstContact, ...normalizedContacts.slice(1)];
}

async function getActiveWhatsAppEndpointByContext(
  params: GetActiveWhatsAppEndpointByContextParams,
): Promise<(fhir5.Endpoint & { id: string }) | null> {
  const organizationId = normalizeOrganizationId(params.organizationId);
  if (!organizationId) return null;

  const bundle = await fhirClients.admin.search<fhir5.Endpoint>("Endpoint", {
    organization: organizationId,
    status: "active",
    _count: "50",
  });

  const endpointCandidates = extractEndpointEntries(bundle);
  const activeCandidate = endpointCandidates.find(
    (candidate) => candidate.status === "active" && Boolean(candidate.id),
  );

  if (!activeCandidate?.id) return null;
  return activeCandidate as fhir5.Endpoint & { id: string };
}

async function createOrUpdateMetaEndpoint(
  params: CreateOrUpdateMetaEndpointParams,
): Promise<fhir5.Endpoint> {
  const organizationId = normalizeOrganizationId(params.organizationId);
  if (!organizationId) {
    throw new Error("organizationId é obrigatório para salvar Endpoint Meta.");
  }

  const wabaId = params.metaConnection.wabaId?.trim();
  if (!wabaId) {
    throw new Error("wabaId é obrigatório para salvar Endpoint Meta.");
  }

  const currentEndpoint = await getActiveWhatsAppEndpointByContext({
    organizationId,
  });

  if (currentEndpoint?.id) {
    return fhirClients.admin.patch<fhir5.Endpoint>(
      "Endpoint",
      currentEndpoint.id,
      [
        { op: "replace", path: "/status", value: "active" },
        {
          op: "replace",
          path: "/name",
          value: params.metaConnection.wabaName ?? currentEndpoint.name,
        },
        {
          op: "replace",
          path: "/managingOrganization",
          value: {
            reference: `Organization/${buildOrganizationReference(organizationId)}`,
          },
        },
        {
          op: "replace",
          path: "/identifier",
          value: [{ system: WABA_IDENTIFIER_SYSTEM, value: wabaId }],
        },
        {
          op: "replace",
          path: "/contact",
          value: buildContactWithRankOne(
            currentEndpoint.contact,
            wabaId,
            params.primaryPhoneValue,
            params.phoneContacts,
          ),
        },
      ],
    );
  }

  const resource: fhir5.Endpoint = {
    resourceType: "Endpoint",
    status: "active",
    address: DEFAULT_ENDPOINT_ADDRESS,
    connectionType: [
      {
        coding: [
          {
            system:
              "http://terminology.hl7.org/CodeSystem/endpoint-connection-type",
            code: "hl7-fhir-rest",
            display: "HL7 FHIR",
          },
        ],
      },
    ],
    name: params.metaConnection.wabaName ?? "WhatsApp Business Endpoint",
    managingOrganization: {
      reference: `Organization/${buildOrganizationReference(organizationId)}`,
    },
    identifier: [{ system: WABA_IDENTIFIER_SYSTEM, value: wabaId }],
    contact: buildContactWithRankOne(
      [],
      wabaId,
      params.primaryPhoneValue,
      params.phoneContacts,
    ),
  };

  return fhirClients.admin.create<fhir5.Endpoint>("Endpoint", resource);
}

async function deactivateEndpointByContext(
  params: GetActiveWhatsAppEndpointByContextParams,
): Promise<void> {
  const currentEndpoint = await getActiveWhatsAppEndpointByContext(params);
  if (!currentEndpoint?.id) return;

  await fhirClients.admin.patch<fhir5.Endpoint>(
    "Endpoint",
    currentEndpoint.id,
    [
      {
        op: "replace",
        path: "/status",
        // Em Endpoint (R5 tipado), não existe "inactive"; "suspended" representa endpoint desativado.
        value: "suspended",
      },
    ],
  );
}

async function getWabaIdRank1ByContext(
  params: GetActiveWhatsAppEndpointByContextParams,
): Promise<string | null> {
  const config = await getWhatsAppEndpointConfigByContext(params);
  return config.wabaIdRank1;
}

type WhatsAppEndpointConfig = {
  wabaIdRank1: string | null;
  phoneNumberIdRank1: string | null;
};

async function getWhatsAppEndpointConfigByContext(
  params: GetActiveWhatsAppEndpointByContextParams,
): Promise<WhatsAppEndpointConfig> {
  const endpoint = await getActiveWhatsAppEndpointByContext(params);
  if (!endpoint) {
    return { wabaIdRank1: null, phoneNumberIdRank1: null };
  }

  return {
    wabaIdRank1: selectWabaIdFromEndpoint(endpoint),
    phoneNumberIdRank1: selectPhoneNumberIdFromEndpoint(endpoint),
  };
}

export const endpointService = {
  getActiveWhatsAppEndpointByContext,
  createOrUpdateMetaEndpoint,
  deactivateEndpointByContext,
  getWhatsAppEndpointConfigByContext,
  getWabaIdRank1ByContext,
};
